/**
 * Состояние и комплектность — закрытые списки, одни на весь проект.
 *
 * Свободным текстом это писали каждый по-своему: «отл», «хорошее, скол»,
 * «б/у». По такому полю нельзя ни отобрать товар на витрине, ни сравнить
 * две вещи в оценке, ни отдать значение площадке — Авито ждёт код, а не
 * фразу. И ИИ, разбирая переписку, должен выбрать из списка, а не выдумать
 * шестую формулировку.
 *
 * Шкала состояния описывает **вид**, и только последняя ступень — работу.
 * Иначе «рабочее» не отличается от «хорошего»: рабочее тут всё, кроме
 * последней ступени.
 *
 * Хранится код, показывается метка. Переименовать метку можно завтра, не
 * трогая данные; коды не меняются никогда — на них смотрят площадки.
 */
export const ITEM_CONDITIONS = [
  { value: 'new', label: 'Новое', hint: 'Не пользовались' },
  { value: 'excellent', label: 'Отличное', hint: 'Следов почти нет' },
  { value: 'good', label: 'Хорошее', hint: 'Следы эксплуатации' },
  { value: 'fair', label: 'Удовлетворительное', hint: 'Заметные повреждения' },
  { value: 'repair', label: 'Требуется ремонт', hint: 'Не работает или работает частично' },
] as const

export const ITEM_COMPLETENESS = [
  { value: 'full', label: 'Полный', hint: 'Коробка и чек' },
  { value: 'box', label: 'Только коробка' },
  { value: 'receipt', label: 'Только чек' },
  { value: 'none', label: 'Без комплекта' },
] as const

export type ItemCondition = (typeof ITEM_CONDITIONS)[number]['value']
export type ItemCompleteness = (typeof ITEM_COMPLETENESS)[number]['value']

/**
 * Ступень словами по её коду.
 *
 * Нужна там, где ступень пришла строкой из базы, а не выбирается в поле:
 * журнал обучения показывал `repair` и `box` — коды, на которые смотрят
 * площадки, а не люди. Не нашли — отдаём как есть: выдумывать перевод коду,
 * которого нет в реестре, значит прятать расхождение.
 */
export function gradeLabel(code: string | null | undefined): string {
  if (!code) return ''

  const found =
    ITEM_CONDITIONS.find((one) => one.value === code) ??
    ITEM_COMPLETENESS.find((one) => one.value === code)

  return found?.label ?? code
}

/**
 * Есть ли у вещи чек — по нему спрашивается дата покупки.
 *
 * Одно правило на форму и на сервер: разойдись они, приёмщик увидел бы поле,
 * которого ручка не принимает, либо наоборот — сохранил бы дату вещи без
 * бумаги, и она пережила бы исправление комплектности.
 */
export const hasReceipt = (value: string | null | undefined): boolean =>
  value === 'full' || value === 'receipt'

const CONDITION_VALUES = ITEM_CONDITIONS.map((c) => c.value) as readonly string[]
const COMPLETENESS_VALUES = ITEM_COMPLETENESS.map((c) => c.value) as readonly string[]

export const isCondition = (value: unknown): value is ItemCondition =>
  typeof value === 'string' && CONDITION_VALUES.includes(value)

export const isCompleteness = (value: unknown): value is ItemCompleteness =>
  typeof value === 'string' && COMPLETENESS_VALUES.includes(value)

/**
 * Состояние словами, вместе с тем, что оно означает: «Хорошее — следы
 * эксплуатации».
 *
 * Нужно там, где значение читает не человек, а модель: ей «хорошее» само
 * по себе ничего не говорит, и она додумывает — «без царапин», «как
 * новый». Подсказка справочника снимает догадку.
 */
export const conditionFull = (value: string | null): string | null => {
  const found = ITEM_CONDITIONS.find((c) => c.value === value)
  return found ? `${found.label} — ${found.hint.toLowerCase()}` : null
}

export const completenessFull = (value: string | null): string | null => {
  const found = ITEM_COMPLETENESS.find((c) => c.value === value)
  if (!found) return null
  return 'hint' in found && found.hint ? `${found.label} — ${found.hint.toLowerCase()}` : found.label
}

export const conditionLabel = (value: string | null): string | null =>
  ITEM_CONDITIONS.find((c) => c.value === value)?.label ?? null

/**
 * Наше состояние — в шкалу площадки.
 *
 * У Авито ступени всего две, и «Новое» у неё означает «не пользовались»: ровно
 * нашу первую ступень. Всё остальное — от отличного до требующего ремонта —
 * это б/у, а насколько именно, площадка спрашивает отдельными полями про
 * царапины и работу устройства.
 *
 * Перевод здесь, а не полем в справочнике: спроси мы состояние дважды, приёмщик
 * заполнил бы его двумя разными словами, и в объявление уехало бы то, которое
 * ближе лежало.
 */
export const platformCondition = (value: string | null | undefined): string =>
  value === 'new' ? 'Новое' : 'Б/у'

/**
 * Работает ли вещь — в терминах площадки.
 *
 * Площадка спрашивает это отдельным обязательным полем: «Включается» или
 * «Не включается». У нас ответ уже есть — последняя ступень шкалы состояния и
 * означает «не работает или работает частично». Спрашивать второй раз значит
 * дать приёмщику два поля про одно и то же и получить «Отличное» вместе с «Не
 * включается».
 *
 * Граница проведена по последней ступени: «Требуется ремонт» уезжает как «Не
 * включается». Строго говоря, частично рабочая вещь включается — но обещать
 * покупателю работу там, где мы сами пометили ремонт, хуже, чем недообещать.
 */
export const platformDeviceWork = (value: string | null | undefined): string =>
  value === 'repair' ? 'Не включается' : 'Включается'

/**
 * Состояние аккумулятора словами — как того просит площадка.
 *
 * У планшетов это обязательное поле со свободным вводом: словаря нет, а без
 * ответа объявление не примут. Спрашивать его у приёмщика значит держать на
 * форме третье поле про батарею — рядом с процентом ёмкости и шкалой
 * состояния, — и получить в объявлении «норм», «ок» и «хз» вместо описания.
 *
 * Ответ собирается из того, что уже известно: процент ёмкости, если его
 * сняли, иначе состояние батареи словами, иначе — ступень состояния.
 * Ремонт называется ремонтом: обещать рабочую батарею там, где вещь
 * помечена нерабочей, нельзя.
 */
export const platformBattery = (
  condition: string | null | undefined,
  percent: unknown,
  health?: unknown,
): string => {
  const value = typeof percent === 'number' ? percent : Number(percent)

  if (Number.isFinite(value) && value > 0) return `Ёмкость ${Math.round(value)}%`

  /* У ноутбука ёмкости нет, есть «Состояние батареи» словами: оно и уходит.
     Иначе «Не держит заряд» уехало бы рядом с «Рабочий» */
  if (typeof health === 'string' && health.trim()) return health

  return condition === 'repair' ? 'Требует замены' : 'Рабочий'
}

/**
 * Комплектация объявления — из того, что уже спросили.
 *
 * Площадка просит галочки «Коробка», «Блок зарядки», «Провод зарядки» (у
 * ноутбуков — «Зарядное устройство», у телевизоров — «Пульт» и «Настольная
 * подставка»). Коробку знает комплектность вещи, зарядку — поле «Аксессуары»,
 * пульт и ножку — свои поля «да / нет». Третий вопрос про то же самое на
 * форме не нужен. Кандидатов больше, чем галочек у одной категории: лишние
 * отсеет анкета площадки.
 */
const CHARGER_PARTS: Record<string, string[]> = {
  'Зарядка (блок + кабель)': ['Блок зарядки', 'Провод зарядки', 'Зарядное устройство'],
  'Только блок питания': ['Блок зарядки', 'Зарядное устройство'],
  'Только кабель': ['Провод зарядки'],
}

export const platformSet = (
  completeness: string | null | undefined,
  attributes: Record<string, unknown>,
): string[] => [
  ...(completeness === 'full' || completeness === 'box' ? ['Коробка'] : []),
  ...(typeof attributes.accessories === 'string' ? (CHARGER_PARTS[attributes.accessories] ?? []) : []),
  ...(attributes.remote === true ? ['Пульт'] : []),
  ...(attributes.stand === true ? ['Настольная подставка'] : []),
]

export const completenessLabel = (value: string | null): string | null =>
  ITEM_COMPLETENESS.find((c) => c.value === value)?.label ?? null

/**
 * Ключи, которые форма спрашивает своими полями, а не характеристикой.
 *
 * Состояние и комплектность стоят в «Общих» справочника, чтобы человек видел:
 * их спрашивают у любой вещи. Заполняются они полями формы по шкалам выше — и,
 * придя ещё и характеристикой, встают в ту же форму вторым комплектом:
 * «Состояние» из справочника и «Состояние вещи» рядом, с разными словами.
 */
export const GRADING_KEYS = ['condition', 'completeness'] as const

/**
 * Поля, которые описывают повреждения вещи.
 *
 * У площадки они названы по-разному в каждой ветке — `ScreenCondition` у
 * телефона, `ScreenFlaws` у телевизора, — но вопрос один: что с экраном и
 * что с корпусом.
 */
export const FLAW_KEYS = [
  'screen_condition',
  'case_condition',
  'kbcondition',
  'screen_flaws',
  'case_flaws',
] as const

/** Ответ, которым эти поля закрываются, когда повреждений нет */
export const NO_FLAWS = 'Без дефектов'

/**
 * Состояние, при котором повреждений быть не может.
 *
 * «Новое» — вещью не пользовались, «Отличное» — следов почти нет. Сколотый
 * угол отличным не бывает: это уже «Хорошее» и ниже. Раз так, спрашивать про
 * дефекты незачем — ответ следует из состояния, а два поля, которые могут
 * противоречить друг другу, рано или поздно противоречат.
 *
 * Правило работает в обе стороны: выбрал состояние — поля повреждений ушли с
 * формы и закрылись сами; отметил трещину — эти две ступени из списка
 * состояний исчезли.
 */
export const isFlawless = (condition: string | null | undefined): boolean =>
  condition === 'new' || condition === 'excellent'

/** Отмечено ли повреждение хоть в одном из полей */
export function hasFlaws(values: Record<string, unknown>): boolean {
  return FLAW_KEYS.some((key) => {
    const value = values[key]
    if (Array.isArray(value)) return value.some((one) => one !== NO_FLAWS)
    return Boolean(value) && value !== NO_FLAWS
  })
}
